export type PresetsAutomationTabId =
  | 'savedViews'
  | 'scheduledReports'
  | 'customizedReports'
  | 'generatedReports'

export type PresetsAutomationTab = {
  id: PresetsAutomationTabId
  label: string
}

export const PRESETS_AUTOMATION_TABS: PresetsAutomationTab[] = [
  { id: 'savedViews', label: 'Saved Views' },
  { id: 'scheduledReports', label: 'Scheduled Reports' },
  { id: 'customizedReports', label: 'Customized Reports' },
  { id: 'generatedReports', label: 'Generated Reports' },
]

/** `?tab=` value for each tab (used with `/report/presets?tab=…` and browser back). */
export const PRESETS_TAB_QUERY: Record<PresetsAutomationTabId, string> = {
  savedViews: 'saved',
  scheduledReports: 'scheduled',
  customizedReports: 'customized',
  generatedReports: 'generated',
}

/** `navigate(…, { state })` from Presets “View” so back can reopen the right tab. */
export type PresetsReturnState = { presetsReturnTab: PresetsAutomationTabId }

export function getPresetsPathWithTab(tab: PresetsAutomationTabId): string {
  return `/report/presets?tab=${PRESETS_TAB_QUERY[tab]}`
}

/** Shared shape for a preset/custom report's preview configuration. */
export type ReportPresetConfig = {
  categoryIds: string[]
  metricIds: string[]
  toolbarSelection: Record<string, string>
  headerBrandEnabled: boolean
}

export type SavedViewRow = {
  id: string
  /** List row + toolbar dropdown — can be a rich, human-friendly label. */
  name: string
  /**
   * Short H1 in the report preview and PDF title. Put location, period, and
   * branch in `preset.toolbarSelection` and the param lines, not in this string.
   */
  previewTitle: string
  dateSaved: string
  lastUpdated: string
  savedBy: string
  /** Pre-defined preview configuration for this saved view. */
  preset: ReportPresetConfig
  /**
   * When set, opening this view loads the standard bundle report preview
   * (Report Preview page) with these route params and toolbar, instead of the
   * custom metric layout (Saved View Preview for `preset` only).
   */
  bundleView?: {
    categoryId: string
    reportSlug: string
    /** Merged on the target page with default toolbar (same as `reportPreviewSelection` state). */
    toolbarSelection: Record<string, string>
  }
}

export type CustomReportRow = {
  id: string
  name: string
  dateSaved: string
  lastUpdated: string
  savedBy: string
  categoryIds: string[]
  metricIds: string[]
  toolbarSelection: Record<string, string>
  headerBrandEnabled: boolean
}

/** Row in Presets → Generated Reports. */
export type GeneratedReportRow = {
  id: string
  name: string
  dateGenerated: string
  /** Display label for schedule status (e.g. Yes / No). */
  schedule: string
  savedBy: string
}

/**
 * When set, the eye icon opens the same full-screen preview the user
 * scheduled from (Report Preview, Saved View preview, or Custom Report Builder).
 */
export type ScheduledReportViewTarget =
  | { kind: 'bundle'; categoryId: string; reportSlug: string }
  | { kind: 'savedView'; savedViewId: string }
  | { kind: 'custom'; customReportId: string }

/** Row in Presets → Scheduled Reports (design mock + Redux; same shape). */
export type ScheduledReportRow = {
  id: string
  name: string
  summaryLine: string
  dateSaved: string
  lastUpdated: string
  savedBy: string
  /** Shown in the Scheduled column. */
  scheduled: boolean
  /** e.g. "Mar 4", "Jan 3". */
  scheduleDate: string
  viewTarget?: ScheduledReportViewTarget
  /**
   * When the in-app "Save Schedule" action created this row; optional on
   * static design mocks. Detail page can fall back to `dateSaved`.
   */
  dateAdded?: string
  /** Optional filters shown/edited in Presets → Edit scheduled report. */
  toolbarSelection?: Record<string, string>
}

/** Route to open the same report UI as when the schedule was created; `null` → use detail page. */
export function getPathForScheduledReportView(
  row: ScheduledReportRow,
): string | null {
  const t = row.viewTarget
  if (!t) return null
  switch (t.kind) {
    case 'bundle':
      return `/report/bundles/${t.categoryId}/${t.reportSlug}/preview`
    case 'savedView':
      return `/report/saved-views/${t.savedViewId}`
    case 'custom':
      return `/report/custom/${t.customReportId}`
  }
}

const FINANCIAL_QUARTER_METRICS: string[] = [
  'gav_summary',
  'gav_ytd',
  'gav_run_rate',
  'gav_variance_trend',
  'gav_production',
  'gav_collection',
  'gav_location',
  'gav_provider',
  'rra_summary',
  'rrisk_summary',
]

const OPERATIONS_LOCATION_METRICS: string[] = [
  'sa_summary',
  'sa_filled_open',
  'sa_hygiene_gaps',
  'sa_production',
  'sa_no_shows',
  'cru_summary',
  'hp_summary',
  'obt_summary',
]

const PROVIDER_PERFORMANCE_METRICS: string[] = [
  'pp_summary',
  'cfe_summary',
  'cmt_summary',
  'te_summary',
  'es_summary',
]

const CLAIMS_ONLY_METRICS: string[] = [
  'claims_summary',
  'rra_summary',
  'rrisk_summary',
]

export const PRESETS_SAVED_VIEWS_MOCK: SavedViewRow[] = [
  {
    id: 'sv-fin-q1',
    name: 'Financial 1st Quarter',
    previewTitle: 'Financial',
    dateSaved: 'Mar 4, 2020',
    lastUpdated: 'Mar 4, 2025',
    savedBy: 'Finance Dept.',
    preset: {
      categoryIds: ['finance'],
      metricIds: FINANCIAL_QUARTER_METRICS,
      toolbarSelection: {
        'period-category': 'historical',
        'period-preset': 'calendar-q1',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-fin-q2',
    name: 'Financial 2nd Quarter',
    previewTitle: 'Financial',
    dateSaved: 'Jun 4, 2020',
    lastUpdated: 'Jun 4, 2025',
    savedBy: 'Finance Dept.',
    preset: {
      categoryIds: ['finance'],
      metricIds: FINANCIAL_QUARTER_METRICS,
      toolbarSelection: {
        'period-category': 'historical',
        'period-preset': 'calendar-q2',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-fin-q3',
    name: 'Financial 3rd Quarter',
    previewTitle: 'Financial',
    dateSaved: 'Sep 4, 2020',
    lastUpdated: 'Sep 4, 2025',
    savedBy: 'Finance Dept.',
    preset: {
      categoryIds: ['finance'],
      metricIds: FINANCIAL_QUARTER_METRICS,
      toolbarSelection: {
        'period-category': 'historical',
        'period-preset': 'calendar-q3',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-ops-loc-1',
    name: 'Operations - Location 1',
    previewTitle: 'Operations',
    dateSaved: 'Mar 5, 2020',
    lastUpdated: 'Mar 5, 2025',
    savedBy: 'Owner',
    preset: {
      categoryIds: ['operations'],
      metricIds: OPERATIONS_LOCATION_METRICS,
      toolbarSelection: {
        'period-category': 'latest-data',
        'period-preset': 'this-month',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-ops-loc-2',
    name: 'Operations - Location 2',
    previewTitle: 'Operations',
    dateSaved: 'Mar 5, 2020',
    lastUpdated: 'Mar 5, 2025',
    savedBy: 'Owner',
    preset: {
      categoryIds: ['operations'],
      metricIds: OPERATIONS_LOCATION_METRICS,
      toolbarSelection: {
        'period-category': 'latest-data',
        'period-preset': 'this-month',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-provider-loc-1-q1',
    name: 'Provider Performance - Location 1 - 1st Quarter',
    previewTitle: 'Provider Performance',
    dateSaved: 'Mar 5, 2020',
    lastUpdated: 'Mar 5, 2025',
    savedBy: 'Owner',
    preset: {
      categoryIds: ['provider-performance'],
      metricIds: PROVIDER_PERFORMANCE_METRICS,
      toolbarSelection: {
        'period-category': 'historical',
        'period-preset': 'calendar-q1',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-provider-loc-1-q2',
    name: 'Provider Performance - Location 1 - 2nd Quarter',
    previewTitle: 'Provider Performance',
    dateSaved: 'Mar 5, 2020',
    lastUpdated: 'Mar 5, 2025',
    savedBy: 'Owner',
    preset: {
      categoryIds: ['provider-performance'],
      metricIds: PROVIDER_PERFORMANCE_METRICS,
      toolbarSelection: {
        'period-category': 'historical',
        'period-preset': 'calendar-q2',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-provider-loc-1-q3',
    name: 'Provider Performance - Location 1 - 3rd Quarter',
    previewTitle: 'Provider Performance',
    dateSaved: 'Sep 4, 2020',
    lastUpdated: 'Sep 4, 2023',
    savedBy: 'Owner',
    preset: {
      categoryIds: ['provider-performance'],
      metricIds: PROVIDER_PERFORMANCE_METRICS,
      toolbarSelection: {
        'period-category': 'historical',
        'period-preset': 'calendar-q3',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-claims-loc-1',
    name: 'Claims Only - Location 1',
    previewTitle: 'Claims Performance',
    dateSaved: 'Jan 4, 2020',
    lastUpdated: 'Jan 4, 2023',
    savedBy: 'Finance Dept.',
    preset: {
      categoryIds: ['finance'],
      metricIds: CLAIMS_ONLY_METRICS,
      toolbarSelection: {
        'period-category': 'latest-data',
        'period-preset': 'this-month',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-claims-loc-3',
    name: 'Claims Only - Location 3',
    previewTitle: 'Claims Performance',
    dateSaved: 'Jan 4, 2020',
    lastUpdated: 'Jan 4, 2023',
    savedBy: 'Finance Dept.',
    preset: {
      categoryIds: ['finance'],
      metricIds: CLAIMS_ONLY_METRICS,
      toolbarSelection: {
        'period-category': 'latest-data',
        'period-preset': 'this-month',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-claims-loc-2',
    name: 'Claims Only - Location 2',
    previewTitle: 'Claims Performance',
    dateSaved: 'Jan 4, 2020',
    lastUpdated: 'Jan 4, 2023',
    savedBy: 'Finance Dept.',
    preset: {
      categoryIds: ['finance'],
      metricIds: CLAIMS_ONLY_METRICS,
      toolbarSelection: {
        'period-category': 'latest-data',
        'period-preset': 'this-month',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
  {
    id: 'sv-claims-loc-5',
    name: 'Claims Only - Location 5',
    previewTitle: 'Claims Performance',
    dateSaved: 'Jan 4, 2020',
    lastUpdated: 'Jan 4, 2022',
    savedBy: 'Finance Dept.',
    preset: {
      categoryIds: ['finance'],
      metricIds: CLAIMS_ONLY_METRICS,
      toolbarSelection: {
        'period-category': 'latest-data',
        'period-preset': 'this-month',
        'provider-filter': 'all-providers',
      },
      headerBrandEnabled: true,
    },
  },
]

/**
 * Design mock: Scheduled Reports tab (listed after user-created Redux rows, newest first).
 * Ids are prefixed with `preset-sch-` so delete is disabled for these rows.
 */
export const PRESETS_SCHEDULED_REPORTS_MOCK: ScheduledReportRow[] = [
  {
    id: 'preset-sch-sv-fin-q1',
    name: 'Financial 1st Quarter',
    summaryLine:
      'Financial 1st Quarter has been scheduled every 12th day of the month.',
    dateSaved: 'Mar 4, 2020',
    lastUpdated: 'Mar 4, 2025',
    savedBy: 'Finance Dept.',
    scheduled: true,
    scheduleDate: 'Mar 4',
    viewTarget: { kind: 'savedView', savedViewId: 'sv-fin-q1' },
  },
  {
    id: 'preset-sch-sv-fin-q2',
    name: 'Financial 2nd Quarter',
    summaryLine:
      'Financial 2nd Quarter has been scheduled every 12th day of the month.',
    dateSaved: 'Jun 4, 2020',
    lastUpdated: 'Jun 4, 2025',
    savedBy: 'Finance Dept.',
    scheduled: true,
    scheduleDate: 'Jun 4',
    viewTarget: { kind: 'savedView', savedViewId: 'sv-fin-q2' },
  },
  {
    id: 'preset-sch-sv-fin-q3',
    name: 'Financial 3rd Quarter',
    summaryLine:
      'Financial 3rd Quarter has been scheduled every 12th day of the month.',
    dateSaved: 'Sep 4, 2020',
    lastUpdated: 'Sep 4, 2025',
    savedBy: 'Finance Dept.',
    scheduled: true,
    scheduleDate: 'Sep 4',
    viewTarget: { kind: 'savedView', savedViewId: 'sv-fin-q3' },
  },
  {
    id: 'preset-sch-sv-ops-loc-1',
    name: 'Operations - Location 1',
    summaryLine:
      'Operations - Location 1 has been scheduled to run every week.',
    dateSaved: 'Mar 5, 2020',
    lastUpdated: 'Mar 5, 2025',
    savedBy: 'Owner',
    scheduled: true,
    scheduleDate: 'Mar 5',
    viewTarget: { kind: 'savedView', savedViewId: 'sv-ops-loc-1' },
  },
  {
    id: 'preset-sch-sv-provider-q3',
    name: 'Provider Performance - Location 1 - 3rd Quarter',
    summaryLine:
      'Provider Performance has been scheduled every 12th day of the month.',
    dateSaved: 'Sep 4, 2020',
    lastUpdated: 'Sep 4, 2023',
    savedBy: 'Owner',
    scheduled: true,
    scheduleDate: 'Sep 4',
    viewTarget: { kind: 'savedView', savedViewId: 'sv-provider-loc-1-q3' },
  },
  {
    id: 'preset-sch-sv-claims-loc-1',
    name: 'Claims Only - Location 1',
    summaryLine: 'Claims Performance has been scheduled to run every week.',
    dateSaved: 'Jan 4, 2020',
    lastUpdated: 'Jan 4, 2023',
    savedBy: 'Finance Dept.',
    scheduled: true,
    scheduleDate: 'Jan 3',
    viewTarget: { kind: 'savedView', savedViewId: 'sv-claims-loc-1' },
  },
  {
    id: 'preset-sch-sv-claims-loc-3',
    name: 'Claims Only - Location 3',
    summaryLine: 'Claims Performance has been scheduled to run every week.',
    dateSaved: 'Jan 4, 2020',
    lastUpdated: 'Jan 4, 2023',
    savedBy: 'Finance Dept.',
    scheduled: true,
    scheduleDate: 'Jan 3',
    viewTarget: { kind: 'savedView', savedViewId: 'sv-claims-loc-3' },
  },
]

export function isPresetScheduledReportRowId(id: string): boolean {
  return id.startsWith('preset-sch-')
}

const CUSTOM_MOCK_OPT_CLAIMS_FINANCE_METRICS: string[] = [
  'sa_summary',
  'sa_filled_open',
  'claims_summary',
  'gav_ytd',
  'gav_production',
  'rra_summary',
]

const CUSTOM_MOCK_CLAIMS_FINANCE_Q3_METRICS: string[] = [
  'claims_summary',
  'gav_summary',
  'gav_ytd',
  'gav_production',
  'rra_summary',
  'rrisk_summary',
]

const CUSTOM_MOCK_OPS_PROVIDER_METRICS: string[] = [
  'sa_summary',
  'sa_production',
  'pp_summary',
  'cfe_summary',
  'cmt_summary',
  'es_summary',
]

const CUSTOM_MOCK_OPS_ENGAGEMENT_METRICS: string[] = [
  'sa_summary',
  'sa_filled_open',
  'rrc_summary',
  'sr_summary',
  'tcf_summary',
]

/**
 * Design mock: Customized Reports tab (above user rows from Redux).
 * Ids use the `preset-cr-` prefix; delete shows a demo-only toast.
 */
export const PRESETS_CUSTOM_REPORTS_MOCK: CustomReportRow[] = [
  {
    id: 'preset-cr-opt-claims-fin-q1',
    name: 'Opt, Claims & Financial 1st Quarter',
    dateSaved: 'Mar 4, 2020',
    lastUpdated: 'Mar 4, 2025',
    savedBy: 'Finance Dept.',
    categoryIds: ['operations', 'finance'],
    metricIds: CUSTOM_MOCK_OPT_CLAIMS_FINANCE_METRICS,
    toolbarSelection: {
      'period-category': 'historical',
      'period-preset': 'calendar-q1',
      'provider-filter': 'all-providers',
    },
    headerBrandEnabled: true,
  },
  {
    id: 'preset-cr-opt-claims-fin-q2',
    name: 'Opt, Claims & Financial 2nd Quarter',
    dateSaved: 'Jun 4, 2020',
    lastUpdated: 'Jun 4, 2025',
    savedBy: 'Finance Dept.',
    categoryIds: ['operations', 'finance'],
    metricIds: CUSTOM_MOCK_OPT_CLAIMS_FINANCE_METRICS,
    toolbarSelection: {
      'period-category': 'historical',
      'period-preset': 'calendar-q2',
      'provider-filter': 'all-providers',
    },
    headerBrandEnabled: true,
  },
  {
    id: 'preset-cr-claims-fin-q3',
    name: 'Claims & Financial 3rd Quarter',
    dateSaved: 'Sep 4, 2020',
    lastUpdated: 'Sep 4, 2025',
    savedBy: 'Finance Dept.',
    categoryIds: ['finance'],
    metricIds: CUSTOM_MOCK_CLAIMS_FINANCE_Q3_METRICS,
    toolbarSelection: {
      'period-category': 'historical',
      'period-preset': 'calendar-q3',
      'provider-filter': 'all-providers',
    },
    headerBrandEnabled: true,
  },
  {
    id: 'preset-cr-ops-provider-loc-1',
    name: 'Operations & Provider Performance - Location 1',
    dateSaved: 'Mar 5, 2020',
    lastUpdated: 'Mar 5, 2025',
    savedBy: 'Owner',
    categoryIds: ['operations', 'provider-performance'],
    metricIds: CUSTOM_MOCK_OPS_PROVIDER_METRICS,
    toolbarSelection: {
      'period-category': 'latest-data',
      'period-preset': 'this-month',
      'provider-filter': 'all-providers',
    },
    headerBrandEnabled: true,
  },
  {
    id: 'preset-cr-ops-provider-loc-1-q3',
    name: 'Operations & Provider Performance - Location 1 - 3rd Quarter',
    dateSaved: 'Sep 4, 2020',
    lastUpdated: 'Sep 4, 2023',
    savedBy: 'Owner',
    categoryIds: ['operations', 'provider-performance'],
    metricIds: CUSTOM_MOCK_OPS_PROVIDER_METRICS,
    toolbarSelection: {
      'period-category': 'historical',
      'period-preset': 'calendar-q3',
      'provider-filter': 'all-providers',
    },
    headerBrandEnabled: true,
  },
  {
    id: 'preset-cr-ops-eng-loc-1',
    name: 'Operations & Engagement - Location 1',
    dateSaved: 'Jan 4, 2020',
    lastUpdated: 'Jan 4, 2023',
    savedBy: 'Finance Dept.',
    categoryIds: ['operations', 'engagement'],
    metricIds: CUSTOM_MOCK_OPS_ENGAGEMENT_METRICS,
    toolbarSelection: {
      'period-category': 'latest-data',
      'period-preset': 'this-month',
      'provider-filter': 'all-providers',
    },
    headerBrandEnabled: true,
  },
  {
    id: 'preset-cr-ops-eng-loc-3',
    name: 'Operations & Engagement  - Location 3',
    dateSaved: 'Jan 4, 2020',
    lastUpdated: 'Jan 4, 2023',
    savedBy: 'Finance Dept.',
    categoryIds: ['operations', 'engagement'],
    metricIds: CUSTOM_MOCK_OPS_ENGAGEMENT_METRICS,
    toolbarSelection: {
      'period-category': 'latest-data',
      'period-preset': 'this-month',
      'provider-filter': 'all-providers',
    },
    headerBrandEnabled: true,
  },
]

export function isPresetCustomReportRowId(id: string): boolean {
  return id.startsWith('preset-cr-')
}

export function isPresetGeneratedReportRowId(id: string): boolean {
  return id.startsWith('preset-gen-')
}

/**
 * Design mock: Generated Reports tab.
 * Ids use the `preset-gen-` prefix for consistency with other preset mocks.
 */
export const PRESETS_GENERATED_REPORTS_MOCK: GeneratedReportRow[] = [
  {
    id: 'preset-gen-opt-claims-fin-q1',
    name: 'Opt, Claims & Financial 1st Quarter',
    dateGenerated: 'Mar 4, 2020',
    schedule: 'Every Friday',
    savedBy: 'Finance Dept.',
  },
  {
    id: 'preset-gen-opt-claims-fin-q2',
    name: 'Opt, Claims & Financial 2nd Quarter',
    dateGenerated: 'Jun 4, 2020',
    schedule: 'Annual',
    savedBy: 'Finance Dept.',
  },
  {
    id: 'preset-gen-claims-fin-q3',
    name: 'Claims & Financial 3rd Quarter',
    dateGenerated: 'Sep 4, 2020',
    schedule: 'Annual',
    savedBy: 'Finance Dept.',
  },
  {
    id: 'preset-gen-ops-provider-loc-1',
    name: 'Operations & Provider Performance - Location 1',
    dateGenerated: 'Mar 5, 2020',
    schedule: 'Quarterly',
    savedBy: 'Owner',
  },
  {
    id: 'preset-gen-ops-provider-loc-1-q3',
    name: 'Operations & Provider Performance - Location 1 - 3rd Quarter',
    dateGenerated: 'Sep 4, 2020',
    schedule: 'Quarterly',
    savedBy: 'Owner',
  },
  {
    id: 'preset-gen-ops-eng-loc-1',
    name: 'Operations & Engagement - Location 1',
    dateGenerated: 'Jan 4, 2020',
    schedule: 'Every 24th',
    savedBy: 'Owner',
  },
  {
    id: 'preset-gen-ops-eng-loc-3',
    name: 'Operations & Engagement - Location 3',
    dateGenerated: 'Jan 4, 2020',
    schedule: 'Every 15th',
    savedBy: 'Finance Dept.',
  },
]

export function isPresetSavedViewRowId(id: string): boolean {
  return PRESETS_SAVED_VIEWS_MOCK.some((r) => r.id === id)
}

export type SavedViewMenuOption = { id: string; label: string }

/** Toolbar dropdown: all mock Saved Views (id + display name). */
export const PRESETS_SAVED_VIEWS_MENU_OPTIONS: SavedViewMenuOption[] =
  PRESETS_SAVED_VIEWS_MOCK.map((v) => ({ id: v.id, label: v.name }))
